type AuthBootstrapSnapshot = {
  startedAt: number | null;
  completedAt: number | null;
  ready: boolean;
  isAuthenticated: boolean;
};

type WaitResult = {
  ready: boolean;
  waitedMs: number;
  timedOut: boolean;
};

const state: AuthBootstrapSnapshot = {
  startedAt: Date.now(),
  completedAt: null,
  ready: false,
  isAuthenticated: false,
};

const waiters: Array<(snapshot: AuthBootstrapSnapshot) => void> = [];

const snapshot = (): AuthBootstrapSnapshot => ({
  startedAt: state.startedAt,
  completedAt: state.completedAt,
  ready: state.ready,
  isAuthenticated: state.isAuthenticated,
});

const flushWaiters = (): void => {
  const current = snapshot();
  while (waiters.length > 0) {
    const resolve = waiters.shift();
    if (resolve) {
      resolve(current);
    }
  }
};

export const markAuthBootstrapStarted = (): void => {
  if (state.startedAt === null) {
    state.startedAt = Date.now();
  }
  state.completedAt = null;
  state.ready = false;
};

export const markAuthBootstrapComplete = (isAuthenticated: boolean): void => {
  if (state.startedAt === null) {
    state.startedAt = Date.now();
  }
  state.completedAt = Date.now();
  state.ready = true;
  state.isAuthenticated = isAuthenticated;
  flushWaiters();
};

export const resetAuthBootstrap = (): void => {
  state.startedAt = null;
  state.completedAt = null;
  state.ready = false;
  state.isAuthenticated = false;
  flushWaiters();
};

export const getAuthBootstrapState = (): AuthBootstrapSnapshot => snapshot();

export const waitForAuthBootstrapReady = async (timeoutMs = 5000): Promise<WaitResult> => {
  if (state.ready) {
    return {
      ready: true,
      waitedMs: 0,
      timedOut: false,
    };
  }

  // No bootstrap in progress yet; avoid blocking indefinitely.
  if (state.startedAt === null) {
    return {
      ready: false,
      waitedMs: 0,
      timedOut: false,
    };
  }

  const startedWaitingAt = Date.now();

  return await new Promise<WaitResult>((resolve) => {
    let settled = false;

    const finish = (ready: boolean, timedOut: boolean): void => {
      if (settled) {
        return;
      }
      settled = true;
      const waitedMs = Date.now() - startedWaitingAt;
      resolve({ ready, waitedMs, timedOut });
    };

    const timeoutId = globalThis.setTimeout(() => {
      finish(state.ready, true);
    }, timeoutMs);

    waiters.push((current) => {
      if (!current.ready) {
        return;
      }
      globalThis.clearTimeout(timeoutId);
      finish(true, false);
    });
  });
};
